import React from 'react';
import isEmpty from 'lodash.isempty';
import cn from 'classnames';
import PropTypes from 'prop-types';

import { short } from '../../common/formatting/date';
import { Button, ErrorBoundary, DropdownButton } from '../../../../components';
import { Diagnoses, Tel, Disposition, CareTeamButton } from '.';
import FACILITY_MAP from '../thsa-name-map.json';

const AdtRow = ({
	userId,
	adt,
	selectedAdt,
	isLoading = false,
	onShowNotes,
	onShowCareTeam,
	onOpenInstavisit,
	onShowPatientADTs,
	onShowCCDA,
	onToggleReadIndicator,
	onToggleStarIndicator,
	disableADTFeatures = false,
	onShowAdditionalDetails,
	hasCampaigns = false,
	onOpenAddToCampaign,
}) => {
	const {
		id,
		patient_first_name,
		patient_last_name,
		patient_gender,
		sending_facility,
		adt_date,
		stat,
		cnt,
		patient_address,
		patient_city,
		patient_state,
		patient_dob,
		patient_phone,
		facility_phone,
		adt_message_type_text,
		disposition_code,
		patient_class_description,
		starred_ind,
		ccda_available_ind,
		employer,
		location,
		program_1,
		prior_cnt,
		primary_payer,
		primary_payer_plan,
		secondary_payer,
		secondary_payer_plan,
		disposition_description,
		campaign_demographic_id,
		provider_last_name,
		provider_first_name,
		...rest
	} = adt;
	const [patientDob] = patient_dob.split('T');
	return (
		<div
			className={cn('row', 'adt-row', {
				'bg-yellow-light': id === selectedAdt.id,
			})}
		>
			<div className="row">
				<div className="col-md-2">
					<h4>
						{patient_first_name} {patient_last_name}{' '}
						{patient_gender && `(${patient_gender})`}
					</h4>
				</div>
				<div className="col-md-10">
					<div className="pull-right adt-actions">
						<Button
							title={`Notes (${cnt ? cnt : 0})`}
							onClick={() => onShowNotes(adt)}
							primary
							small
							leftIcon="edit"
							disabled={isLoading}
						/>
						{!disableADTFeatures && onToggleReadIndicator && (
							<Button
								leftIcon={stat == 10 ? 'archive' : 'undo'}
								className="btn-archive"
								disabled={isLoading}
								small
								title={stat == 10 ? 'Archive' : 'Restore'}
								onClick={() => onToggleReadIndicator(id, stat == 10)}
							/>
						)}
						{!disableADTFeatures && onToggleStarIndicator && (
							<Button
								leftIcon={starred_ind ? 'star-o' : 'star'}
								className="btn-favorite"
								disabled={isLoading}
								small
								title={starred_ind ? 'Unstar' : 'Star'}
								onClick={() => onToggleStarIndicator(id, starred_ind)}
							/>
						)}
					</div>
					<h4>
						<strong>ADT Type:</strong>
						<span>&nbsp;{adt_message_type_text}&nbsp;&nbsp;&nbsp;</span>
						{!patient_class_description ? (
							''
						) : (
							<React.Fragment>
								<strong>ADT Class:</strong>
								<span>&nbsp;{patient_class_description}</span>
							</React.Fragment>
						)}
					</h4>
				</div>
			</div>
			<div className="row pt">
				<div className="col-md-2">
					<Tel telString={patient_phone} />
					<br />
					DOB: {short(patientDob, false, 'PP')}
					<br />
					<address>
						{patient_address}
						<br />
						{patient_city}, {patient_state}
					</address>
				</div>
				<div className="col-md-7">
					<p className="discharge">
						<ErrorBoundary fallback="Unknown Disposition Code">
							<Disposition
								code={disposition_code}
								description={disposition_description}
							/>
						</ErrorBoundary>
					</p>
					{isEmpty(sending_facility) ? (
						<strong>FACILITY MISSING</strong>
					) : (
						<strong>
							{FACILITY_MAP[sending_facility] || sending_facility}
						</strong>
					)}
					{isEmpty(facility_phone) ? (
						<span> - NO FACILITY PHONE</span>
					) : (
						<span>
							{' '}
							- <Tel telString={facility_phone} />
						</span>
					)}
					<br />
					{short(adt_date, false, 'PPpp')}
					<Diagnoses {...rest} />
					{!isEmpty(primary_payer) && (
						<React.Fragment>
							{primary_payer}, {primary_payer_plan}
							<br />
						</React.Fragment>
					)}
					{!isEmpty(secondary_payer) && (
						<React.Fragment>
							{secondary_payer}, {secondary_payer_plan}
							<br />
						</React.Fragment>
					)}
				</div>
				<div className="col-md-3">
					{!isEmpty(provider_last_name) && !isEmpty(provider_first_name) && (
						<>
							<strong>Provider:</strong> {provider_last_name},{' '}
							{provider_first_name}
							<br />
						</>
					)}
					{!isEmpty(employer) && (
						<React.Fragment>
							<strong>Employer:</strong> {employer}
							<br />
						</React.Fragment>
					)}
					{!isEmpty(location) && (
						<React.Fragment>
							<strong>Location:</strong> {location}
							<br />
						</React.Fragment>
					)}
					{!isEmpty(program_1) && (
						<React.Fragment>
							<strong>Special Program:</strong> {program_1}
							<br />
						</React.Fragment>
					)}
				</div>
			</div>
			<div className="row">
				<div className="col-md-12 pt">
					<CareTeamButton
						userId={userId}
						adtId={id}
						onClick={() => onShowCareTeam(adt)}
						onCareTeamLoaded={() => {}}
					/>
					<Button
						className="mr-xl"
						title="Text-a-Visit"
						onClick={() =>
							onOpenInstavisit({
								...adt,
								patient_dob: short(patientDob, false, 'yyyy-MM-dd'),
							})
						}
						primary
					/>
					<Button
						className="mr-xl"
						title="Additional Details"
						onClick={() => onShowAdditionalDetails(adt)}
					/>
					{Boolean(ccda_available_ind) && (
						<div className="ccda-dropdown-container mr-xl">
							<DropdownButton
								title="CCDA"
								primary
								containerClassName="ccda-dropdown"
								items={[
									{
										title: 'Full CCDA',
										onClick: () => onShowCCDA(id),
									},
									{
										title: 'Summary CCDA',
										onClick: () => onShowCCDA(id, false),
									},
								]}
							/>
						</div>
					)}
					{Boolean(prior_cnt) && (
						<Button
							className="mr-xl"
							title={`Patient ADTs (${prior_cnt})`}
							onClick={() => onShowPatientADTs(adt)}
						/>
					)}
					{hasCampaigns && (
						<Button
							title={
								campaign_demographic_id ? 'Update Campaigns' : 'Add Campaign'
							}
							onClick={() => onOpenAddToCampaign(adt)}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

AdtRow.propTypes = {
	userId: PropTypes.number.isRequired,
	adt: PropTypes.object,
	selectedAdt: PropTypes.object,
	isLoading: PropTypes.bool,
	onShowNotes: PropTypes.func,
	onShowCareTeam: PropTypes.func,
	onOpenInstavisit: PropTypes.func,
	onShowPatientADTs: PropTypes.func,
	onShowCCDA: PropTypes.func,
	onToggleReadIndicator: PropTypes.func,
	onToggleStarIndicator: PropTypes.func,
	disableADTFeatures: PropTypes.bool,
	onShowAdditionalDetails: PropTypes.func,
	hasCampaigns: PropTypes.bool,
	onOpenAddToCampaign: PropTypes.func,
};

export default AdtRow;
